// Ops → Integration & API: the company's self-serve integration hub. Everything a company needs to plug
// Movorra into its own stack lives here — the inbound (server-to-server) leads API with copy-paste snippets
// + a live tester, the public CHAT API + embeddable widget / micro-frontend / web-component code, and the
// outbound completed-lead (CRM push) webhook. A Movorra super-admin can switch which company they're viewing.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader, Button } from '../ui/primitives';
import {
  getConsoleAccess, getTenantApiKey, rotateTenantApiKey, activeTenant,
  listFlows, getFlowKey, getTenantOutbound, setTenantOutbound, connections,
  getTenantSecrets, putTenantSecret, deleteTenantSecret, startSession, sendMessage,
  getTenantChannels, setTenantChannels, testTenantChannel, verifyTenantEmail, tenantEmailStatus,
  getAiTraceRecent, getAiTraceSession,
  getIdentityProvider, setIdentityProvider, testIdentityToken,
} from '../services/ecapApi';

const BASE = process.env.REACT_APP_ECAP_API_URL || 'http://localhost:8080';

// The inbound (server-to-server) API surface — drives both the curl snippets and the live tester.
const ENDPOINTS = [
  {
    id: 'list', method: 'GET', path: '/api/v1/leads',
    desc: 'List captured leads, newest first, paginated. Optionally filter by flow and/or lead status.',
    query: [
      { k: 'flowId', ph: 'contact-capture', hint: 'filter to one flow' },
      { k: 'status', ph: 'NEW | IN_PROGRESS | RESOLVED | ARCHIVED', hint: 'filter by status' },
      { k: 'limit', ph: '50', hint: '1–200' },
      { k: 'offset', ph: '0', hint: 'page offset' },
    ],
  },
  {
    id: 'get', method: 'GET', path: '/api/v1/leads/{id}',
    desc: 'Fetch a single lead by its id (business values + AI enrichment split out).',
    pathParams: [{ k: 'id', ph: 'lead id from the list' }],
  },
  {
    id: 'update', method: 'PUT', path: '/api/v1/leads/{id}',
    desc: 'Update a lead’s lifecycle from your own app: status, assignee, and/or internal note.',
    pathParams: [{ k: 'id', ph: 'lead id' }],
    body: [
      { k: 'status', ph: 'IN_PROGRESS', hint: 'NEW | IN_PROGRESS | RESOLVED | ARCHIVED' },
      { k: 'assignee', ph: 'crm-bot' },
      { k: 'note', ph: 'synced to Salesforce' },
    ],
  },
];

function buildUrl(ep, vals) {
  let path = ep.path;
  (ep.pathParams || []).forEach((p) => { path = path.replace(`{${p.k}}`, encodeURIComponent(vals[p.k] || `{${p.k}}`)); });
  const qs = (ep.query || []).map((q) => [q.k, (vals[q.k] || '').trim()]).filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return BASE + path + (qs ? `?${qs}` : '');
}
function buildBody(ep, vals) {
  if (!ep.body) return null;
  const o = {};
  ep.body.forEach((b) => { const v = (vals[b.k] || '').trim(); if (v) o[b.k] = v; });
  return Object.keys(o).length ? o : null;
}
function curlSnippet(ep, vals, key, reveal) {
  const url = buildUrl(ep, vals);
  const body = buildBody(ep, vals);
  // BUG-04: don't print the real server-to-server key in the copy-paste snippet until it's revealed.
  const shown = (reveal && key) ? key : '<YOUR_API_KEY>';
  const lines = [`curl -X ${ep.method} "${url}" \\`, `  -H "X-Movorra-Api-Key: ${shown}"`];
  if (body) { lines[lines.length - 1] += ' \\'; lines.push('  -H "Content-Type: application/json" \\', `  -d '${JSON.stringify(body)}'`); }
  return lines.join('\n');
}

const Section = ({ title, subtitle, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5">
    <div className="font-semibold text-slate-800 text-sm">{title}</div>
    {subtitle && <div className="text-xs text-slate-500 mb-3 mt-0.5">{subtitle}</div>}
    <div className={subtitle ? '' : 'mt-3'}>{children}</div>
  </div>
);

function Snippet({ code, onCopy }) {
  return (
    <div className="relative">
      <pre className="text-[11px] bg-slate-900 text-slate-100 rounded-lg p-2.5 pr-14 overflow-x-auto whitespace-pre">{code}</pre>
      <button className="absolute top-2 right-2 text-[11px] bg-slate-700 text-slate-100 px-2 py-0.5 rounded hover:bg-slate-600" onClick={() => onCopy(code)}>copy</button>
    </div>
  );
}

const TABS = [['apis', 'APIs'], ['embed', 'Embed & MFE'], ['outbound', 'Outbound'], ['notifications', 'Notifications'], ['identity', 'Tenant Identity'], ['traces', 'AI Traces'], ['secrets', 'Secrets']];

function OpsIntegration({ authSession, readOnly = false }) {
  // The active company is set once in the sidebar; this page reads it (and remounts on switch).
  const [tenant, setTenant] = useState(activeTenant());
  const [key, setKey] = useState('');
  const [reveal, setReveal] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('apis');

  useEffect(() => {
    if (!activeTenant()) getConsoleAccess(authSession).then((w) => w?.tenant && setTenant(w.tenant)).catch(() => {});
  }, [authSession]);

  useEffect(() => {
    if (tenant) getTenantApiKey(tenant, authSession).then((d) => setKey(d?.apiKey || '')).catch(() => {});
  }, [tenant, authSession]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 1400); };
  const copy = (text) => { navigator.clipboard?.writeText(text).then(() => flash('Copied')).catch(() => {}); };
  const rotate = async () => {
    if (!tenant || !window.confirm('Rotate the API key? Apps using the current key stop working until updated.')) return;
    setBusy(true);
    try { const d = await rotateTenantApiKey(tenant, authSession); setKey(d?.apiKey || ''); flash('Rotated'); }
    catch (e) { flash(`Error: ${e.message}`); } finally { setBusy(false); }
  };
  const masked = key ? `${key.slice(0, 11)}${'•'.repeat(14)}${key.slice(-4)}` : '';

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader title="Integration & API" subtitle="Plug Movorra into your stack — inbound API, chat widget, and CRM push" />

      {/* API key */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Your API key {msg && <span className="ml-2 text-emerald-600 normal-case">· {msg}</span>}</div>
        <div className="flex gap-2 items-center">
          <code className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] font-mono truncate">{key ? (reveal ? key : masked) : 'Loading…'}</code>
          <Button size="sm" variant="outline" onClick={() => setReveal((r) => !r)}>{reveal ? 'Hide' : 'Reveal'}</Button>
          <Button size="sm" variant="outline" onClick={() => copy(key)} disabled={!key}>Copy</Button>
          <Button size="sm" variant="outline" onClick={rotate} disabled={busy || !tenant || readOnly}>Rotate</Button>
        </div>
        <div className="text-[11px] text-slate-400 mt-1.5">Send as the <code className="font-mono">X-Movorra-Api-Key</code> header. Maps to <b>{tenant || '…'}</b> only. Base URL: <code className="font-mono">{BASE}</code></div>
      </div>

      {/* Segregated sections */}
      <div className="flex gap-1.5 mb-4 border-b border-slate-200">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{l}</button>
        ))}
      </div>

      {tab === 'apis' && (
        <>
          <Section title="Inbound API — read & work your leads" subtitle="Server-to-server. Call these from your backend with the API key above.">
            <div className="space-y-4">
              {ENDPOINTS.map((ep) => <EndpointCard key={ep.id} ep={ep} apiKey={key} reveal={reveal} onCopy={copy} />)}
            </div>
          </Section>
          <ChatEmbed mode="api" authSession={authSession} tenant={tenant} onCopy={copy} />
          <LiveApis tenant={tenant} onCopy={copy} />
        </>
      )}
      {tab === 'embed' && (
        <>
          <ChatEmbed mode="embed" authSession={authSession} tenant={tenant} onCopy={copy} />
          <Mfes tenant={tenant} apiKey={key} reveal={reveal} onCopy={copy} />
        </>
      )}
      {tab === 'outbound' && <OutboundEditor authSession={authSession} tenant={tenant} readOnly={readOnly} onFlash={flash} />}
      {tab === 'notifications' && <NotificationsEditor authSession={authSession} tenant={tenant} readOnly={readOnly} onFlash={flash} />}
      {tab === 'identity' && <IdentityProvider authSession={authSession} tenant={tenant} readOnly={readOnly} onFlash={flash} />}
      {tab === 'traces' && <TraceExplorer authSession={authSession} tenant={tenant} onFlash={flash} />}
      {tab === 'secrets' && <Secrets authSession={authSession} tenant={tenant} readOnly={readOnly} onFlash={flash} />}
    </div>
  );
}

// Your company's own isolated secret store (its own AWS Secrets Manager secret in production). Store API
// keys / tokens here and reference them from a Connection's auth as ${secret:KEY} — values never leave.
function Secrets({ authSession, tenant, readOnly, onFlash }) {
  const [keys, setKeys] = useState([]);
  const [k, setK] = useState('');
  const [v, setV] = useState('');
  const [loadError, setLoadError] = useState(null); // OPSINT-SECRETS-LOAD-SWALLOWED
  const load = () => {
    if (!tenant) return;
    setLoadError(null);
    getTenantSecrets(tenant, authSession).then((d) => setKeys(d?.keys || [])).catch((e) => setLoadError(e?.message || 'Could not load your secrets.'));
  };
  useEffect(load, [tenant, authSession]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!k.trim()) return;
    // A successful write returns the authoritative key list, so clear any stale load-error banner (the Secrets
    // tab has no Refresh button, so load() otherwise wouldn't clear it until a tenant/session change).
    try { const d = await putTenantSecret(tenant, k.trim(), v, authSession); setKeys(d?.keys || keys); setLoadError(null); setK(''); setV(''); onFlash('Saved'); }
    catch (e) { onFlash(`Error: ${e.message}`); }
  };
  const del = async (key) => {
    try { const d = await deleteTenantSecret(tenant, key, authSession); setKeys(d?.keys || keys.filter((x) => x !== key)); setLoadError(null); onFlash('Removed'); }
    catch (e) { onFlash(`Error: ${e.message}`); }
  };
  return (
    <Section title="Secrets — your company's isolated secret store" subtitle={`Its own AWS Secrets Manager secret. Store credentials here, reference them from a Connection as \${secret:KEY}.`}>
      {loadError && <div className="text-xs text-rose-600 mb-3">⚠ Could not load your secrets ({loadError}). The list may be incomplete — retry.</div>}
      {keys.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-3">
          {keys.map((key) => (
            <span key={key} className="inline-flex items-center gap-1.5 text-[12px] bg-slate-100 rounded-lg px-2 py-1 font-mono">
              {key}
              {!readOnly && <button className="text-rose-500 hover:text-rose-600" onClick={() => del(key)} title="Delete">×</button>}
            </span>
          ))}
        </div>
      ) : (!loadError && <div className="text-xs text-slate-400 mb-3">No secrets yet.</div>)}
      {!readOnly && (
        <div className="flex gap-2 items-center">
          <input className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-mono w-40" placeholder="key (e.g. stripe_key)" value={k} onChange={(e) => setK(e.target.value)} />
          <input className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-mono flex-1" type="password" placeholder="value (write-only)" value={v} onChange={(e) => setV(e.target.value)} />
          <Button size="sm" variant="outline" onClick={add} disabled={!k.trim()}>Add</Button>
        </div>
      )}
      <div className="text-[11px] text-slate-400 mt-1.5">Values are write-only — only key names are ever shown. A Connection with auth value <code className="font-mono">{`\${secret:stripe_key}`}</code> resolves from here at call time.</div>
    </Section>
  );
}

// Tenant identity provider (OIDC / SSO) — Continuous Customer Thread. A generic, vertical-agnostic setting:
// the tenant points at its own issuer and picks the claim that becomes the end-user's identity, so a signed-in
// user is recognised across sessions & channels and can resume a live-agent / approval conversation instead of
// starting over. A blank issuer or the toggle off falls back to the platform Cognito default. Tokens are
// validated server-side (signature, expiry, and — for a custom issuer — an exact issuer match so one tenant's
// provider can never resolve into another's).
function IdentityProvider({ authSession, tenant, readOnly, onFlash }) {
  const [custom, setCustom] = useState(false);
  const [issuer, setIssuer] = useState('');
  const [jwksUri, setJwksUri] = useState('');
  const [audience, setAudience] = useState('');
  const [claim, setClaim] = useState('email');
  const [emailVerified, setEmailVerified] = useState(true);
  const [loaded, setLoaded] = useState(false);
  // *-LOAD-SWALLOWED-OVERWRITE: a swallowed load failure left the form in its DEFAULT (custom=false, blank
  // issuer) state that looks like "platform default" — a Save then wrote enabled:false and disabled the real
  // OIDC provider. Track the load error and BLOCK save until a successful load.
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Token tester (validates against the SAVED config).
  const [token, setToken] = useState('');
  const [testClaim, setTestClaim] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!tenant) return;
    setLoaded(false); setLoadError(null);
    getIdentityProvider(authSession).then((d) => {
      // The platform default comes back as enabled=true with a blank issuer; treat "custom" as enabled AND an issuer.
      setCustom(!!(d && d.enabled && d.issuer && d.issuer.trim()));
      setIssuer(d?.issuer || '');
      setJwksUri(d?.jwksUri || '');
      setAudience(d?.audience || '');
      setClaim(d?.identityClaim || 'email');
      setEmailVerified(d?.emailVerifiedRequired !== false);
      setLoaded(true); // mark loaded ONLY on success — a failure must not present a saveable default form
    }).catch((e) => setLoadError(e?.message || 'Could not load the identity settings.'));
  }, [tenant, authSession]);

  const save = async () => {
    if (loadError) { onFlash('Refresh first — the identity settings could not be loaded, so saving would overwrite them.'); return; }
    if (custom && !issuer.trim()) { onFlash('Issuer is required for a custom provider'); return; }
    setSaving(true);
    try {
      await setIdentityProvider({
        issuer: custom ? issuer.trim() : '',
        jwksUri: custom ? jwksUri.trim() : '',
        audience: custom ? audience.trim() : '',
        identityClaim: (claim || 'email').trim(),
        emailVerifiedRequired: emailVerified,
        enabled: custom,
      }, authSession);
      onFlash('Saved');
    } catch (e) { onFlash(`Error: ${e.message}`); } finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!token.trim()) return;
    setTesting(true); setResult(null);
    try { setResult(await testIdentityToken(token.trim(), testClaim.trim() || null, authSession)); }
    catch (e) { setResult({ valid: false, error: e.message }); } finally { setTesting(false); }
  };

  const inp = 'rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm w-full';
  const claimIsEmail = (claim || 'email').trim().toLowerCase() === 'email';

  return (
    <>
      <Section title="Identity provider (SSO)"
        subtitle="Recognise a signed-in user across sessions and channels — so your end-users can resume a live-agent or approval conversation instead of starting over. Use your own OIDC/OAuth provider, or the Movorra platform default.">
        {loadError ? <div className="text-xs text-rose-600">⚠ Could not load the identity settings ({loadError}). Refresh before editing — saving now would overwrite the saved provider.</div>
          : !loaded ? <div className="text-xs text-slate-400">Loading…</div> : (
          <>
            <label className="flex items-center gap-2 mb-3 text-sm">
              <input type="checkbox" checked={custom} disabled={readOnly} onChange={(e) => setCustom(e.target.checked)} />
              <span className="font-medium text-slate-700">Bring your own identity provider</span>
              <span className={`text-[11px] px-1.5 py-0.5 rounded ${custom ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                {custom ? 'Custom OIDC' : 'Platform default (Movorra Cognito)'}
              </span>
            </label>

            {custom && (
              <div className="space-y-3 pl-1">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Issuer <span className="text-rose-400">*</span></div>
                  <input className={inp} placeholder="https://login.your-company.com/" value={issuer}
                    disabled={readOnly} onChange={(e) => setIssuer(e.target.value)} />
                  <div className="text-[11px] text-slate-400 mt-1">A token is accepted only when its <code className="font-mono">iss</code> matches this exactly — one company&apos;s provider can never resolve into another&apos;s.</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">JWKS URI <span className="text-slate-300 normal-case">(optional)</span></div>
                  <input className={inp} placeholder="auto-discovered from the issuer's /.well-known/openid-configuration" value={jwksUri}
                    disabled={readOnly} onChange={(e) => setJwksUri(e.target.value)} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Audience <span className="text-slate-300 normal-case">(optional)</span></div>
                  <input className={inp} placeholder="leave blank to skip the audience check" value={audience}
                    disabled={readOnly} onChange={(e) => setAudience(e.target.value)} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Identity claim</div>
                  <input className={`${inp} font-mono`} placeholder="email" value={claim}
                    disabled={readOnly} onChange={(e) => setClaim(e.target.value)} />
                  <div className="text-[11px] text-slate-400 mt-1">Which token claim becomes the customer identity — e.g. <code className="font-mono">email</code>, <code className="font-mono">sub</code>, <code className="font-mono">phone_number</code>.</div>
                </div>
                {claimIsEmail && (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={emailVerified} disabled={readOnly} onChange={(e) => setEmailVerified(e.target.checked)} />
                    <span className="text-slate-600">Require <code className="font-mono">email_verified</code> (else fall back to <code className="font-mono">sub</code>)</span>
                  </label>
                )}
                {!issuer.trim() && <div className="text-[11px] text-amber-600">Issuer is required — until it&apos;s set, users fall back to the platform default.</div>}
              </div>
            )}

            {!custom && (
              <div className="text-[11px] text-slate-400">Your end-users are identified by their Movorra-issued sign-in (email, verified). Turn this on to trust your own provider&apos;s tokens instead.</div>
            )}

            {!readOnly && (
              <div className="mt-4"><Button size="sm" onClick={save} disabled={saving || (custom && !issuer.trim())}>{saving ? 'Saving…' : 'Save'}</Button></div>
            )}
          </>
        )}
      </Section>

      <Section title="Test a token"
        subtitle="Validate a sample user token against your SAVED configuration and see the identity it resolves to — no guesswork before you go live. Save changes above first.">
        <textarea className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-mono w-full h-24"
          placeholder="paste a JWT / OIDC id_token here" value={token} onChange={(e) => setToken(e.target.value)} />
        <div className="flex gap-2 items-center mt-2">
          <input className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-mono w-56"
            placeholder="claim override (optional)" value={testClaim} onChange={(e) => setTestClaim(e.target.value)} />
          <Button size="sm" variant="outline" onClick={runTest} disabled={testing || !token.trim()}>{testing ? 'Testing…' : 'Test token'}</Button>
        </div>
        {result && (
          <div className={`mt-3 rounded-lg border p-3 text-sm ${result.valid ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
            {result.valid ? (
              <>
                <div className="text-emerald-700 font-medium">✓ Valid — identity: <code className="font-mono">{result.identity}</code></div>
                <div className="text-[12px] text-slate-600 mt-1">
                  from claim <code className="font-mono">{result.claim}</code>
                  {result.issuer ? <> · issuer <code className="font-mono">{result.issuer}</code></> : null}
                  {result.source ? <> · source <code className="font-mono">{result.source}</code></> : null}
                </div>
                {result.claims && Object.keys(result.claims).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-slate-500 cursor-pointer">token claims</summary>
                    <pre className="text-[11px] bg-white border border-slate-200 rounded p-2 mt-1 overflow-x-auto">{JSON.stringify(result.claims, null, 2)}</pre>
                  </details>
                )}
              </>
            ) : (
              <div className="text-rose-700">✗ {result.error || 'Invalid token'}</div>
            )}
          </div>
        )}
      </Section>
    </>
  );
}

// The three remotely-mountable micro-frontends: drop an iframe (or the chat bundle) into any Next/React/
// Angular app. Framework-agnostic + style-isolated. Leads/Analytics MFEs are token-authenticated iframes.
function Mfes({ tenant, apiKey, reveal, onCopy }) {
  const shownKey = (reveal && apiKey) ? apiKey : '<API_KEY>'; // BUG-04: mask the key in the iframe snippet until revealed
  const chat = `// Chat — style-isolated bundle, mount anywhere:\n<div id="chat"></div>\n<script src="${BASE}/embed/widget.js"></script>\n<script>MovorraChat.mount('#chat', { key: 'mk_live_<flow-key>', api: '${BASE}' });</script>`;
  const leads = `<!-- Manage-leads MFE — mounts in your CRM/back-office -->\n<iframe\n  src="${BASE}/embed/leads-mfe.html?key=${shownKey}&api=${BASE}"\n  style="width:100%;height:520px;border:0"></iframe>`;
  const analytics = `<!-- Analytics MFE -->\n<iframe\n  src="${BASE}/embed/analytics-mfe.html?key=${shownKey}&api=${BASE}"\n  style="width:100%;height:420px;border:0"></iframe>`;
  const agentConsole = `<!-- Live agent console MFE — operators claim & chat live with customers on human handoff.\n     Drop it into your CRM/back-office. Auth via a per-operator token (Bearer). -->\n<iframe\n  src="${BASE}/embed/agent-console.html?api=${BASE}&token=<OPERATOR_TOKEN>&tenant=${tenant || '<TENANT>'}"\n  style="width:100%;height:640px;border:0"></iframe>`;
  const chatPage = `# Hosted customer chat page — a full-screen, linkable "Chat with us" (live handoff built in).\n# Link it from email / SMS / a QR code / your help center — no code to embed:\n${BASE}/embed/chat-page.html?key=mk_live_<flow-key>&title=Chat%20with%20us&accent=4a90d9\n\n<!-- …or iframe it into any page: -->\n<iframe\n  src="${BASE}/embed/chat-page.html?key=mk_live_<flow-key>"\n  style="width:100%;height:640px;border:0"></iframe>`;
  return (
    <Section title="Embeddable MFEs — mount Movorra inside your app" subtitle="Framework-agnostic micro-frontends for Next.js / React / Angular / plain HTML.">
      <div className="space-y-3">
        <Block label="Chat MFE (live)"><Snippet code={chat} onCopy={onCopy} /></Block>
        <Block label="Hosted chat page — customer-facing, linkable (email/SMS/QR), live handoff built in"><Snippet code={chatPage} onCopy={onCopy} /></Block>
        <Block label="Live agent console MFE — operators pick up & chat on human handoff"><Snippet code={agentConsole} onCopy={onCopy} /></Block>
        <Block label="Manage-leads MFE (iframe, authenticated by your API key)"><Snippet code={leads} onCopy={onCopy} /></Block>
        <Block label="Analytics MFE"><Snippet code={analytics} onCopy={onCopy} /></Block>
      </div>
      <div className="text-[11px] text-slate-500 mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5">
        <b>Security:</b> the leads/analytics iframes authenticate with your API key — mount them only in your own trusted, authenticated back-office. In production, prefer a short-lived scoped token minted per session rather than the master key.
      </div>
    </Section>
  );
}

// Live handoff + human-approval + AI-decision APIs. These are the SAME endpoints the Studio Live/Approvals
// consoles and the agent-console MFE call — surfaced here so teams can build their own operator tooling.
// Operator endpoints are Bearer-token (staff JWT); the two customer-side endpoints are public (flow key).
function LiveApis({ tenant, onCopy }) {
  const t = tenant || '<TENANT>';
  const opHdr = `-H "Authorization: Bearer <OPERATOR_TOKEN>"` + (tenant ? ` -H "X-Act-As-Tenant: ${t}"` : '');
  const queue = [
    `# Waiting + my claimed live chats (operator poll or bootstrap)`,
    `curl "${BASE}/studio/api/live/queue" ${opHdr}`,
    ``,
    `# Long-poll for instant handoff pop-ups — holds ~25s, returns on the first event`,
    `curl "${BASE}/studio/api/live/poll" ${opHdr}`,
  ].join('\n');
  const claim = [
    `# Claim a waiting chat (first-come, atomic) → returns the transcript`,
    `curl -X POST "${BASE}/studio/api/live/{id}/claim" ${opHdr}`,
    ``,
    `# Send a message to the customer`,
    `curl -X POST "${BASE}/studio/api/live/{id}/messages" ${opHdr} \\`,
    `  -H "Content-Type: application/json" -d '{"text":"Hi — I can help with that."}'`,
    ``,
    `# ✨ AI-drafted reply from the transcript (nothing is auto-sent)`,
    `curl -X POST "${BASE}/studio/api/live/{id}/suggest" ${opHdr}`,
    ``,
    `# Hand the conversation back to the bot (resumes the flow)`,
    `curl -X POST "${BASE}/studio/api/live/{id}/handback" ${opHdr} \\`,
    `  -H "Content-Type: application/json" -d '{"message":""}'`,
  ].join('\n');
  const approvals = [
    `# Human-approval inbox (flow blocked on human://approve)`,
    `curl "${BASE}/studio/api/approvals/queue" ${opHdr}`,
    ``,
    `# Resolve — approve/deny writes the decision + resumes the flow`,
    `curl -X POST "${BASE}/studio/api/approvals/{id}/resolve" ${opHdr} \\`,
    `  -H "Content-Type: application/json" -d '{"decision":"approve","note":"Refund OK"}'`,
  ].join('\n');
  const customer = [
    `# Customer side (public — flow key, no staff token). Poll the live transcript…`,
    `curl "${BASE}/runtime/sessions/{id}/transcript"`,
    ``,
    `# …and long-poll for near-instant operator messages (holds ~25s, signal-only)`,
    `curl "${BASE}/runtime/sessions/{id}/live-poll"`,
    ``,
    `# Share a file in the chat (image/PDF, <=15MB) — ai://vision can then read it, operators see it`,
    `curl -X POST "${BASE}/runtime/sessions/{id}/attachments" -F "file=@photo.jpg"`,
    `# List / download shared files`,
    `curl "${BASE}/runtime/sessions/{id}/attachments"`,
    `curl "${BASE}/runtime/sessions/{id}/attachments/{attachmentId}" -o file.jpg`,
  ].join('\n');
  return (
    <Section title="Live handoff & approvals API — build your own operator tooling"
      subtitle="Powers the Studio Live/Approvals consoles + the agent-console MFE. Operator calls use a staff Bearer token; the two customer endpoints are public (flow key).">
      <div className="space-y-3">
        <Block label="Queue & long-poll (operator)"><Snippet code={queue} onCopy={onCopy} /></Block>
        <Block label="Claim · send · AI-suggest · hand back (operator)"><Snippet code={claim} onCopy={onCopy} /></Block>
        <Block label="Human-approval inbox & resolve (operator)"><Snippet code={approvals} onCopy={onCopy} /></Block>
        <Block label="Customer live transcript & long-poll (public)"><Snippet code={customer} onCopy={onCopy} /></Block>
      </div>
      <div className="text-[11px] text-slate-500 mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5">
        Prefer push? Configure an outbound <b>webhook</b> for <code className="font-mono">live.handoff.requested/claimed/resolved</code> and <code className="font-mono">approval.requested/resolved</code> — set <code className="font-mono">liveWebhookUrl</code> (+ optional <code className="font-mono">liveWebhookSecret</code> for an HMAC-SHA256 <code className="font-mono">X-Movorra-Signature</code>) in the <b>Secrets</b> tab. Full schemas live in Swagger under the <b>Live</b> tag.
      </div>
    </Section>
  );
}

// The tenant-level outbound object: fires on EVERY completed lead. Auth via a reusable Connection,
// dynamic payload via a {{field}} body template. Supersedes the bare webhook URL (kept as a fallback).
function OutboundEditor({ authSession, tenant, readOnly, onFlash }) {
  const [o, setO] = useState({ enabled: true, connectionRef: '', url: '', method: 'POST', bodyTemplate: '' });
  const [conns, setConns] = useState([]);
  // OUTBOUND-LOAD-SWALLOWED-OVERWRITE: the config GET failure was swallowed and there was no load guard, so a
  // Save persisted the blank default over the real CRM-push config. Only enable Save once the load succeeds.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [connsError, setConnsError] = useState(null); // OPSINT-OUTBOUND-CONNS-LOAD-SWALLOWED
  const [saving, setSaving] = useState(false); // OPSINT-OUTBOUND-SAVE-DOUBLE-SUBMIT
  useEffect(() => {
    if (!tenant) return;
    setLoaded(false); setLoadError(null); setConnsError(null);
    getTenantOutbound(tenant, authSession).then((d) => {
      if (d?.outbound) setO({
        enabled: d.outbound.enabled !== false, connectionRef: d.outbound.connectionRef || '',
        url: d.outbound.url || '', method: d.outbound.method || 'POST', bodyTemplate: d.outbound.bodyTemplate || '',
      });
      setLoaded(true);
    }).catch((e) => setLoadError(e?.message || 'Could not load the outbound settings.'));
    // OPSINT-OUTBOUND-CONNS-LOAD-SWALLOWED: a swallowed connections-load left the Connection (auth) dropdown
    // silently EMPTY, so a user configuring a new CRM push couldn't attach auth and saved connectionRef='' →
    // the completed-lead push goes out unauthenticated. Surface the failure so the empty dropdown is explained.
    connections.list(authSession).then((c) => setConns(c || [])).catch((e) => setConnsError(e?.message || 'Could not load your Connections.'));
  }, [tenant, authSession]);
  const set = (k, v) => setO((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!loaded || saving) { if (!loaded) onFlash('Refresh first — the outbound settings could not be loaded, so saving would overwrite them.'); return; }
    setSaving(true);
    try { const d = await setTenantOutbound(tenant, o, authSession); if (d?.outbound) set('url', d.outbound.url || ''); onFlash('Saved'); }
    catch (e) { onFlash(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };
  return (
    <Section title="Outbound — CRM push on lead completion" subtitle="Fires on every completed lead (all flows). Auth via a Connection, custom payload via a template.">
      {loadError && <div className="text-xs text-rose-600 mb-2">⚠ Could not load the outbound settings ({loadError}). Refresh before editing — saving now would overwrite the saved config.</div>}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-[11px] text-slate-500 flex items-center gap-2 col-span-2">
          <input type="checkbox" checked={o.enabled} onChange={(e) => set('enabled', e.target.checked)} disabled={readOnly} /> Enabled
        </label>
        <label className="text-[11px] text-slate-500">Connection (auth)
          <select className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" value={o.connectionRef} onChange={(e) => set('connectionRef', e.target.value)} disabled={readOnly}>
            <option value="">— none (no auth) —</option>
            {conns.map((c) => <option key={c.id} value={c.id}>{c.name || c.id} ({c.authType})</option>)}
          </select>
          {connsError && <span className="block mt-0.5 text-[11px] text-rose-600">⚠ Couldn't load your Connections ({connsError}) — retry; the list may be incomplete.</span>}
        </label>
        <label className="text-[11px] text-slate-500">Method
          <select className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" value={o.method} onChange={(e) => set('method', e.target.value)} disabled={readOnly}>
            {['POST', 'PUT', 'PATCH'].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="text-[11px] text-slate-500 col-span-2">Endpoint URL <span className="text-slate-300">absolute, or a path appended to the Connection base URL</span>
          <input className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" value={o.url} placeholder="https://your-crm.example.com/leads  (or  /leads)" onChange={(e) => set('url', e.target.value)} disabled={readOnly} />
        </label>
        <label className="text-[11px] text-slate-500 col-span-2">Payload template <span className="text-slate-300">JSON with {'{{field}}'} placeholders — leave blank for the default payload</span>
          <textarea className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm font-mono" rows={4} value={o.bodyTemplate}
            placeholder={'{\n  "name": "{{fullName}}",\n  "email": "{{email}}",\n  "priority": "{{priority}}",\n  "flow": "{{flowId}}"\n}'}
            onChange={(e) => set('bodyTemplate', e.target.value)} disabled={readOnly} />
        </label>
      </div>
      <div className="text-[11px] text-slate-400 mt-1.5">Placeholders: any captured attribute (<code className="font-mono">{'{{email}}'}</code>), AI enrichment (<code className="font-mono">{'{{_sentiment}}'}</code>, <code className="font-mono">{'{{_summary}}'}</code>), and <code className="font-mono">{'{{flowId}} {{sessionId}} {{identity}} {{priority}} {{completedAt}}'}</code>.</div>
      <div className="mt-2"><Button size="sm" onClick={save} disabled={readOnly || !loaded || saving}>{saving ? 'Saving…' : 'Save outbound'}</Button></div>
    </Section>
  );
}

// Notification channels — the sales team gets pinged the moment a lead lands. Multiple emails, multiple
// webhooks, and more than one channel of a type. Each recipient can be tested + removed individually.
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());
const isUrl = (s) => /^https?:\/\/.+/.test((s || '').trim());
const validRecipient = (type, s) => (type === 'EMAIL' ? isEmail(s) : isUrl(s));
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `c${Date.now()}${Math.round(Math.random() * 1e6)}`);

function NotificationsEditor({ authSession, tenant, readOnly, onFlash }) {
  const [channels, setChannels] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false); // OPSINT-NOTIFS-SAVE-DOUBLE-SUBMIT
  // CHANNELS-LOAD-SWALLOWED-OVERWRITE: a swallowed load failure rendered "No channels yet" (channels=[]) and a
  // Save then REPLACED the tenant's real channel list with the empty one. Mark loaded ONLY on success; on
  // failure record loadError and block save.
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    if (!tenant) return;
    setLoaded(false); setLoadError(null);
    getTenantChannels(tenant, authSession)
      .then((d) => { setChannels(Array.isArray(d?.channels) ? d.channels : []); setLoaded(true); })
      .catch((e) => setLoadError(e?.message || 'Could not load the notification channels.'));
  }, [tenant, authSession]);

  const patch = (id, p) => setChannels((cs) => cs.map((c) => (c.id === id ? { ...c, ...p } : c)));
  const addChannel = (type) => setChannels((cs) => [...cs, { id: newId(), type, enabled: true, format: 'HTML', recipients: [], secretRef: '' }]);
  const removeChannel = (id) => setChannels((cs) => cs.filter((c) => c.id !== id));

  const save = async () => {
    if (!loaded || saving) { if (!loaded) onFlash('Refresh first — the channels could not be loaded, so saving would overwrite them.'); return; }
    setSaving(true);
    try { const d = await setTenantChannels(tenant, channels, authSession); if (Array.isArray(d?.channels)) setChannels(d.channels); onFlash('Notifications saved'); }
    catch (e) { onFlash(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Section title="Notifications — ping the team on a new lead"
      subtitle="Fires on every completed lead (all flows). Add email addresses and webhook URLs; each is delivered independently — one bad recipient never blocks the rest.">
      {loadError && <div className="text-sm text-rose-600 mb-2">⚠ Could not load the notification channels ({loadError}). Refresh before editing — saving now would overwrite the saved channels.</div>}
      {!loaded && !loadError && <div className="text-sm text-slate-400">Loading…</div>}
      {loaded && channels.length === 0 && <div className="text-sm text-slate-400 mb-2">No channels yet. Add one below.</div>}
      <div className="space-y-3">
        {channels.map((c) => (
          <ChannelCard key={c.id} c={c} tenant={tenant} authSession={authSession} readOnly={readOnly}
            onPatch={(p) => patch(c.id, p)} onRemove={() => removeChannel(c.id)} onFlash={onFlash} />
        ))}
      </div>
      {!readOnly && (
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => addChannel('EMAIL')}>+ Email</Button>
          <Button size="sm" variant="outline" onClick={() => addChannel('WEBHOOK')}>+ Webhook</Button>
          <div className="flex-1" />
          <Button size="sm" onClick={save} disabled={readOnly || !loaded || saving}>{saving ? 'Saving…' : 'Save notifications'}</Button>
        </div>
      )}
    </Section>
  );
}

function ChannelCard({ c, tenant, authSession, readOnly, onPatch, onRemove, onFlash }) {
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState({}); // recipient -> 'ok'|'fail'|'testing'
  const [verif, setVerif] = useState({});   // email -> 'VERIFIED'|'PENDING'|'UNVERIFIED'
  const isEmailCh = c.type === 'EMAIL';
  const label = isEmailCh ? 'email address' : 'webhook URL';
  const recips = (c.recipients || []).join(',');

  // SES sandbox only sends to verified recipients — load each email's verification status for its badge.
  useEffect(() => {
    if (!isEmailCh || !tenant || !(c.recipients || []).length) { setVerif({}); return; }
    tenantEmailStatus(tenant, c.recipients, authSession)
      .then((d) => setVerif(d?.statuses || {}))
      .catch(() => {});
  }, [isEmailCh, tenant, authSession, recips]); // eslint-disable-line react-hooks/exhaustive-deps

  const verify = async (email) => {
    const prev = verif[email]; // VERIFY-OPTIMISTIC-NOT-REVERTED: restore on failure — no email was sent, so a
    setVerif((v) => ({ ...v, [email]: 'PENDING' })); // stuck "⏳ pending" badge would be misleading
    try {
      const d = await verifyTenantEmail(tenant, email, authSession);
      setVerif((v) => ({ ...v, [email]: d?.status || 'PENDING' }));
      onFlash(d?.status === 'VERIFIED' ? 'Already verified ✓' : `Verification email sent to ${email} — ask them to click it`);
    } catch (e) { onFlash(`Verify failed: ${e.message}`); setVerif((v) => ({ ...v, [email]: prev })); }
  };

  const addRecipient = () => {
    const v = draft.trim();
    if (!v) return;
    if (!validRecipient(c.type, v)) { onFlash(`Not a valid ${label}`); return; }
    if ((c.recipients || []).includes(v)) { setDraft(''); return; }
    onPatch({ recipients: [...(c.recipients || []), v] });
    setDraft('');
  };
  const removeRecipient = (v) => onPatch({ recipients: (c.recipients || []).filter((r) => r !== v) });

  const test = async (recipient) => {
    setStatus((s) => ({ ...s, [recipient]: 'testing' }));
    try {
      const d = await testTenantChannel(tenant, c, recipient, authSession);
      const ok = d?.result?.ok;
      setStatus((s) => ({ ...s, [recipient]: ok ? 'ok' : 'fail' }));
      onFlash(ok ? 'Test sent ✓' : `Test failed: ${d?.result?.detail || 'error'}`);
    } catch (e) { setStatus((s) => ({ ...s, [recipient]: 'fail' })); onFlash(`Test failed: ${e.message}`); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${isEmailCh ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>{c.type}</span>
        <label className="text-[11px] text-slate-500 flex items-center gap-1"><input type="checkbox" checked={c.enabled !== false} onChange={(e) => onPatch({ enabled: e.target.checked })} disabled={readOnly} />enabled</label>
        {isEmailCh && (
          <label className="text-[11px] text-slate-500 flex items-center gap-1 ml-1">format
            <select className="rounded border border-slate-200 px-1 py-0.5 text-xs" value={c.format || 'HTML'} onChange={(e) => onPatch({ format: e.target.value })} disabled={readOnly}>
              <option value="HTML">HTML (readable)</option>
              <option value="JSON">JSON (machine)</option>
            </select>
          </label>
        )}
        <div className="flex-1" />
        {!readOnly && <button className="text-[11px] text-rose-400 hover:text-rose-600" onClick={onRemove}>remove channel</button>}
      </div>
      <div className="space-y-1">
        {(c.recipients || []).length === 0 && <div className="text-xs text-slate-300">No recipients yet.</div>}
        {(c.recipients || []).map((r) => (
          <div key={r} className="flex items-center gap-2 text-sm">
            <span className="font-mono text-slate-700 flex-1 truncate">{r}</span>
            {isEmailCh && verif[r] === 'VERIFIED' && <span className="text-[11px] text-emerald-600">✓ verified</span>}
            {isEmailCh && verif[r] === 'PENDING' && <span className="text-[11px] text-amber-600" title="A verification email was sent — the recipient must click the link">⏳ pending</span>}
            {isEmailCh && (verif[r] === 'UNVERIFIED' || verif[r] === undefined) && (
              <button className="text-[11px] text-amber-600 hover:text-amber-800" onClick={() => verify(r)} title="SES sandbox: send this address a one-click verification email">Verify</button>
            )}
            {status[r] === 'ok' && <span className="text-[11px] text-emerald-600">✓ sent</span>}
            {status[r] === 'fail' && <span className="text-[11px] text-rose-500">failed</span>}
            {status[r] === 'testing' && <span className="text-[11px] text-slate-400">testing…</span>}
            {/* OPSINT-CHANNELCARD-TEST-DOUBLE-SUBMIT: disable while a real test send is in flight. */}
            <button className="text-[11px] text-indigo-500 hover:text-indigo-700 disabled:opacity-40" disabled={status[r] === 'testing'} onClick={() => test(r)}>Test</button>
            {!readOnly && <button className="text-[11px] text-slate-300 hover:text-rose-500" onClick={() => removeRecipient(r)}>✕</button>}
          </div>
        ))}
      </div>
      {!readOnly && (
        <div className="flex gap-2 mt-2">
          <input className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-sm" placeholder={isEmailCh ? 'name@company.com' : 'https://your-endpoint.example.com/hook'}
            value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }} />
          <Button size="sm" variant="outline" onClick={addRecipient}>Add</Button>
        </div>
      )}
    </div>
  );
}

// AI Trace Explorer — every LLM call across ALL channels (chat widget, inbound API, tester), so you can
// open a real session and see exactly what the model received/returned, tokens, and latency, and export it.
const downloadJson = (name, data) => {
  try {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { /* ignore */ }
};
const shortId = (s) => (s ? s.slice(0, 8) : '—');
const traceTime = (ms) => { try { return new Date(ms).toLocaleString(); } catch (e) { return ''; } };

function TraceExplorer({ authSession, tenant, onFlash }) {
  const [recent, setRecent] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [flowNames, setFlowNames] = useState({}); // flowId -> human name
  const [modalSession, setModalSession] = useState(null); // session id opened in the popup
  const [trace, setTrace] = useState([]);
  const [traceLoading, setTraceLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null); // OPSINT-TRACE-RECENT-SWALLOWED
  const traceReqRef = useRef(null); // OPSINT-TRACE-SESSION-RACE: latest opened session id
  const load = () => {
    setLoaded(false); setLoadErr(null);
    getAiTraceRecent(authSession).then((r) => setRecent(Array.isArray(r) ? r : [])).catch((e) => setLoadErr(e?.message || 'Could not load traces.')).finally(() => setLoaded(true));
    listFlows(authSession).then((fs) => {
      const m = {}; (fs || []).forEach((f) => { m[f.id] = f.name || f.id; }); setFlowNames(m);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [authSession, tenant]); // eslint-disable-line react-hooks/exhaustive-deps
  const openSess = (sid) => {
    if (!sid) return;
    // OPSINT-TRACE-SESSION-RACE: clicking session A then B fast could let A's trace resolve last and overwrite
    // the trace shown for B. Only apply a response that is still the session the operator is viewing.
    traceReqRef.current = sid;
    setModalSession(sid); setTrace([]); setTraceLoading(true);
    getAiTraceSession(sid, authSession)
      .then((r) => { if (traceReqRef.current === sid) setTrace(Array.isArray(r) ? r : []); })
      .catch(() => { if (traceReqRef.current === sid) setTrace([]); })
      .finally(() => { if (traceReqRef.current === sid) setTraceLoading(false); });
  };
  const flowLabel = (id) => (id ? (flowNames[id] || id) : '—');
  const copy = (t) => { try { navigator.clipboard.writeText(t || ''); onFlash('Copied'); } catch (e) { /* ignore */ } };

  return (
    <Section title="AI traces — every LLM call, all channels"
      subtitle="Chat widget, inbound API, and the tester all land here. Click a session to open its full trace in a window — exactly what went to/from the model, tokens and latency — to check why a live conversation failed.">
      <div className="flex justify-between items-center mb-2">
        <Button size="sm" variant="ghost" onClick={load}>Refresh</Button>
        <Button size="sm" variant="ghost" onClick={() => downloadJson('ai-traces-recent.json', recent)} disabled={!recent.length}>Export recent</Button>
      </div>
      {loadErr && <div className="text-sm text-rose-600 mb-2">⚠ Could not load AI traces ({loadErr}). Retry with Refresh.</div>}
      {!loaded && <div className="text-sm text-slate-400">Loading…</div>}
      {loaded && !loadErr && recent.length === 0 && <div className="text-sm text-slate-400">No AI calls yet — they appear here as conversations run (widget / API / tester).</div>}
      {recent.length > 0 && (
        <div className="rounded-lg border border-slate-200 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="text-left px-2 py-1.5 font-medium">Time</th>
              <th className="text-left px-2 py-1.5 font-medium">Session</th>
              <th className="text-left px-2 py-1.5 font-medium">Flow</th>
              <th className="text-left px-2 py-1.5 font-medium">Type</th>
              <th className="text-left px-2 py-1.5 font-medium">Model</th>
              <th className="text-right px-2 py-1.5 font-medium">Tokens in/out</th>
              <th className="text-right px-2 py-1.5 font-medium">Latency</th>
            </tr></thead>
            <tbody>
              {recent.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => openSess(c.sessionId)} title={c.sessionId}>
                  <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{traceTime(c.createdAt)}</td>
                  <td className="px-2 py-1 font-mono text-slate-600 truncate max-w-[160px]">{c.sessionId || '—'}</td>
                  <td className="px-2 py-1 text-slate-700 truncate max-w-[160px]">{flowLabel(c.flowId)}</td>
                  <td className="px-2 py-1 text-indigo-600 font-medium">{c.type}</td>
                  <td className="px-2 py-1 text-slate-400">{c.model}</td>
                  <td className="px-2 py-1 text-right text-slate-500">{c.inputTokens}/{c.outputTokens}</td>
                  <td className="px-2 py-1 text-right text-slate-400">{c.latencyMs}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modalSession && (
        <TraceModal sessionId={modalSession} calls={trace} loading={traceLoading} flowLabel={flowLabel}
          onCopy={copy} onExport={() => downloadJson(`ai-trace-${shortId(modalSession)}.json`, trace)} onClose={() => setModalSession(null)} />
      )}
    </Section>
  );
}

// Full-session AI trace shown in a centered popup window. Backdrop click / ✕ / Esc closes it;
// each call renders its exact prompt + response (copyable), tokens and latency. Export dumps JSON.
function TraceModal({ sessionId, calls, loading, flowLabel, onCopy, onExport, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center p-4 md:p-8 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start gap-3 px-4 py-3 border-b border-slate-100">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">AI trace · {calls.length} call{calls.length === 1 ? '' : 's'}</div>
            <div className="text-[11px] text-slate-500 font-mono truncate" title={sessionId}>{sessionId}</div>
          </div>
          <div className="flex gap-2 items-center shrink-0">
            <Button size="sm" variant="ghost" onClick={onExport} disabled={!calls.length}>Export</Button>
            <button className="text-slate-400 hover:text-slate-700 text-lg leading-none px-1" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>
        <div className="overflow-auto p-4 space-y-2">
          {loading && <div className="text-sm text-slate-400">Loading trace…</div>}
          {!loading && calls.length === 0 && <div className="text-sm text-slate-400">No AI calls recorded for this session.</div>}
          {calls.map((c, i) => (
            <div key={c.id || i} className="border border-slate-100 rounded-lg p-2.5">
              <div className="flex items-center gap-2 text-xs mb-1.5 flex-wrap">
                <span className="font-semibold text-indigo-600">{c.callType}</span>
                <span className="text-slate-400">{c.model}</span>
                {c.flowId && <span className="text-slate-500">· {flowLabel(c.flowId)}</span>}
                <span className="flex-1" />
                <span className="text-slate-400">in {c.inputTokens} · out {c.outputTokens} tok · {c.latencyMs}ms</span>
              </div>
              <TraceIO label="Prompt (to model)" text={c.prompt} onCopy={() => onCopy(c.prompt)} />
              <TraceIO label="Response (from model)" text={c.response} onCopy={() => onCopy(c.response)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TraceIO({ label, text, onCopy }) {
  return (
    <div className="mt-1">
      <div className="flex justify-between mb-0.5"><span className="text-[11px] text-slate-500">{label}</span><button className="text-[11px] text-indigo-500 hover:text-indigo-700" onClick={onCopy}>copy</button></div>
      <pre className="text-[11px] bg-slate-900 text-slate-100 rounded p-2 overflow-auto whitespace-pre-wrap max-h-60">{text || '(empty)'}</pre>
    </div>
  );
}

const METHOD_TONE = { GET: 'bg-emerald-100 text-emerald-700', PUT: 'bg-amber-100 text-amber-700', POST: 'bg-blue-100 text-blue-700' };

function EndpointCard({ ep, apiKey, reveal, onCopy }) {
  const [vals, setVals] = useState({});
  const [resp, setResp] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  // The live tester (send) still uses the real apiKey; only the copy-paste snippet is masked until reveal (BUG-04).
  const snippet = useMemo(() => curlSnippet(ep, vals, apiKey, reveal), [ep, vals, apiKey, reveal]);

  const send = async () => {
    setSending(true); setResp(null);
    try {
      const body = buildBody(ep, vals);
      const r = await fetch(buildUrl(ep, vals), {
        method: ep.method,
        headers: { 'X-Movorra-Api-Key': apiKey, ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const text = await r.text();
      let pretty = text; try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch (e) { /* raw */ }
      setResp({ status: r.status, text: pretty });
    } catch (e) { setResp({ status: 0, text: `Request failed: ${e.message}` }); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${METHOD_TONE[ep.method]}`}>{ep.method}</span>
        <code className="text-[13px] font-mono text-slate-700">{ep.path}</code>
      </div>
      <div className="text-xs text-slate-500 mb-3">{ep.desc}</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {(ep.pathParams || []).map((p) => (
          <label key={p.k} className="text-[11px] text-slate-500">{p.k} <span className="text-rose-400">*</span>
            <input className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" placeholder={p.ph} value={vals[p.k] || ''} onChange={(e) => set(p.k, e.target.value)} />
          </label>
        ))}
        {(ep.query || []).map((q) => (
          <label key={q.k} className="text-[11px] text-slate-500">{q.k} <span className="text-slate-300">{q.hint}</span>
            <input className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" placeholder={q.ph} value={vals[q.k] || ''} onChange={(e) => set(q.k, e.target.value)} />
          </label>
        ))}
        {(ep.body || []).map((b) => (
          <label key={b.k} className="text-[11px] text-slate-500">{b.k} <span className="text-slate-300">{b.hint || 'body'}</span>
            <input className="w-full mt-0.5 rounded-md border border-slate-200 px-2 py-1 text-sm" placeholder={b.ph} value={vals[b.k] || ''} onChange={(e) => set(b.k, e.target.value)} />
          </label>
        ))}
      </div>
      <Snippet code={snippet} onCopy={onCopy} />
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" onClick={send} disabled={sending || !apiKey}>{sending ? 'Sending…' : 'Try it'}</Button>
        {resp && <span className={`text-xs font-medium ${resp.status >= 200 && resp.status < 300 ? 'text-emerald-600' : 'text-rose-600'}`}>HTTP {resp.status || '—'}</span>}
      </div>
      {resp && <pre className="mt-2 text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-2.5 max-h-64 overflow-auto whitespace-pre-wrap">{resp.text}</pre>}
    </div>
  );
}

// Public chat: pick a published flow → its endpoint key (mk_live_) → the runtime API + the three embed methods.
function ChatEmbed({ authSession, tenant, onCopy, mode = 'api' }) {
  const [flows, setFlows] = useState([]);
  const [flowId, setFlowId] = useState('');
  const [key, setKey] = useState('');
  useEffect(() => {
    // Reset to the (new) company's first flow whenever the tenant changes, so we never carry a stale
    // flowId from a previously-viewed company into the key lookup below.
    listFlows(authSession).then((f) => { setFlows(f || []); setFlowId(f && f[0] ? f[0].id : ''); }).catch(() => { setFlows([]); setFlowId(''); });
  }, [authSession, tenant]);
  useEffect(() => {
    // OPSINT-CHATEMBED-FLOWKEY-RACE: guard against a stale getFlowKey landing last and leaving the WRONG
    // flow's public key in the copy-paste embed snippet after a fast flow switch.
    if (!flowId) { setKey(''); return undefined; }
    let alive = true;
    getFlowKey(flowId, authSession).then((d) => { if (alive) setKey(d?.publicKey || ''); }).catch(() => { if (alive) setKey(''); });
    return () => { alive = false; };
  }, [flowId, authSession]);

  const k = key || 'mk_live_...';
  const restApi = [
    `# 1) start a session (per-flow endpoint key, no login)`,
    `curl -X POST "${BASE}/runtime/sessions" -H "X-Movorra-Key: ${k}"`,
    `# → { "sessionId": "..." }`,
    ``,
    `# 2) send a message (session id is the capability token)`,
    `curl -X POST "${BASE}/runtime/sessions/SID/messages" \\`,
    `  -H "Content-Type: application/json" -d '{"text":"I need a cab to the airport"}'`,
    ``,
    `# streaming: GET ${BASE}/runtime/sessions/SID/stream?message=... (SSE)`,
  ].join('\n');
  const bubble = `<script src="${BASE}/embed/widget.js" data-key="${k}"></script>`;
  const mfe = [
    `<div id="chat"></div>`,
    `<script src="${BASE}/embed/widget.js"></script>`,
    `<script>`,
    `  var chat = MovorraChat.mount('#chat', { key: '${k}', api: '${BASE}' });`,
    `  chat.on && chat.on('complete', function (e) { /* lead captured → your CRM */ });`,
    `</script>`,
  ].join('\n');
  const webComponent = `<script src="${BASE}/embed/widget.js"></script>\n<movorra-chat key="${k}" title="Support"></movorra-chat>`;

  const title = mode === 'api' ? 'Chat API' : 'Chat embed';
  const subtitle = mode === 'api'
    ? 'Run a flow as a conversation over the runtime API. Pick a published flow to fill in its endpoint key.'
    : 'Drop the chat into your own site — bubble, inline micro-frontend, or web component.';
  return (
    <Section title={title} subtitle={subtitle}>
      <label className="text-[11px] text-slate-500 block mb-3">Flow
        <select className="ml-2 rounded-lg border border-slate-200 px-2 py-1 text-sm" value={flowId} onChange={(e) => setFlowId(e.target.value)}>
          {flows.map((f) => <option key={f.id} value={f.id}>{f.name || f.id}</option>)}
        </select>
        <span className="ml-2 font-mono text-[11px] text-slate-400">{key ? `${key.slice(0, 12)}…` : 'no key yet'}</span>
      </label>
      <div className="space-y-3">
        {mode === 'api' ? (
          <>
            <Block label="Raw chat API (server or client)"><Snippet code={restApi} onCopy={onCopy} /></Block>
            <Block label="Live tester — start session &amp; send messages"><ChatTester flowKey={key} /></Block>
          </>
        ) : (
          <>
            <Block label="Drop-in chat bubble"><Snippet code={bubble} onCopy={onCopy} /></Block>
            <Block label="Inline micro-frontend (mount in your layout, get events)"><Snippet code={mfe} onCopy={onCopy} /></Block>
            <Block label="Web component"><Snippet code={webComponent} onCopy={onCopy} /></Block>
          </>
        )}
      </div>
    </Section>
  );
}

// Live exercise of the two core chat endpoints: POST /runtime/sessions (start) + POST .../messages (send).
function ChatTester({ flowKey }) {
  const [sid, setSid] = useState('');
  const [msgs, setMsgs] = useState([]); // { role: 'user'|'bot', text }
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const start = async () => {
    if (!flowKey) return;
    setBusy(true); setMsgs([]);
    try { const d = await startSession(flowKey); setSid(d?.sessionId || ''); setMsgs(d?.greeting ? [{ role: 'bot', text: d.greeting }] : []); }
    catch (e) { setMsgs([{ role: 'bot', text: `Error: ${e.message}` }]); } finally { setBusy(false); }
  };
  const send = async () => {
    const t = input.trim(); if (!t || !sid) return;
    setInput(''); setMsgs((m) => [...m, { role: 'user', text: t }]); setBusy(true);
    try { const d = await sendMessage(sid, t); setMsgs((m) => [...m, { role: 'bot', text: d?.reply || '(no reply)' }]); }
    catch (e) { setMsgs((m) => [...m, { role: 'bot', text: `Error: ${e.message}` }]); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-2.5">
      <div className="flex items-center gap-2 mb-2">
        <Button size="sm" variant="outline" onClick={start} disabled={busy || !flowKey}>{sid ? 'Restart' : 'Start session'}</Button>
        {sid && <span className="text-[11px] font-mono text-slate-400">session {sid.slice(0, 8)}…</span>}
      </div>
      {msgs.length > 0 && (
        <div className="space-y-1.5 mb-2 max-h-56 overflow-auto">
          {msgs.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
              <span className={`inline-block rounded-lg px-2.5 py-1 text-[13px] ${m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800'}`}>{m.text}</span>
            </div>
          ))}
        </div>
      )}
      {sid && (
        <div className="flex gap-2">
          <input className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-sm" placeholder="Type a message…" value={input}
            onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} disabled={busy} />
          <Button size="sm" onClick={send} disabled={busy || !input.trim()}>Send</Button>
        </div>
      )}
    </div>
  );
}

const Block = ({ label, children }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>{children}</div>
);

export default OpsIntegration;
